import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';
import { useSaleFormContext } from './SaleFormContext';

const SELECT_STYLES = {
    indicatorsContainer: (provided) => ({ ...provided, padding: '0px', height: '38px' }),
    valueContainer: (provided) => ({ ...provided, padding: '0 8px' }),
    placeholder: (provided) => ({ ...provided, fontSize: '12px' }),
    control: (provided, state) => ({
        ...provided,
        fontSize: '14px',
        minHeight: '38px',
        height: '38px',
        ...(state.isDisabled ? {
            backgroundColor: '#f4f6fc',
            borderColor: '#dde3f5',
            cursor: 'not-allowed',
        } : {}),
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.isDisabled ? 'rgba(1, 50, 137, 0.45)' : provided.color,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        borderRadius: '10px',
        boxShadow: '0 8px 28px rgba(1, 50, 137, 0.15)',
        overflow: 'hidden',
        border: '1px solid rgba(1, 50, 137, 0.12)',
        padding: 0,
    }),
    menuList: (provided) => ({ ...provided, padding: 0 }),
    option: (provided, state) => ({
        ...provided,
        fontSize: '13px',
        padding: '6px 12px',
        backgroundColor: state.isSelected
            ? state.isFocused ? 'rgba(1,50,137,0.14)' : 'rgba(1,50,137,0.09)'
            : state.isFocused ? '#eef2ff' : 'white',
        color: state.isSelected ? 'var(--appcolor)' : '#1a1a2e',
        fontWeight: state.isSelected ? 600 : 400,
        cursor: 'pointer',
    }),
};

function PaymentMethodCard({ title, suffix, options, onChangeType, onBlurAmount, onChangePreload, onBlurExpiration, headerRight, requiredKey }) {
    const { t } = useTranslation();
    const { isFieldDisabled, isFieldRequired } = useSaleFormContext();
    const { control, register, formState: { errors } } = useFormContext();

    const typeName = `paymentType${suffix}`;
    const amountName = `paymentAmount${suffix}`;
    const preloadName = `preloadTransaction${suffix}`;
    const expirationName = `preloadExpiration${suffix}`;

    return (
        <div className="card paymentMethodCard">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {t(title)}
                {headerRight}
            </div>
            <div className="card-body">
                <div className="row g-2">
                    <div className={`col-md-8 selectInputContainer form-group ${requiredKey && isFieldRequired(requiredKey) ? 'required' : ''}`}>
                        <label className="control-label inputlabel">{t('PaymentTypes')}{suffix}</label>
                        <Controller
                            render={({ field }) => (
                                <CreatableSelect {...field}
                                    styles={SELECT_STYLES}
                                    isClearable
                                    isDisabled={isFieldDisabled(typeName)}
                                    onChange={onChangeType}
                                    options={options}
                                    className={`form-control ${errors[typeName] ? 'is-invalid' : ''}`}
                                />
                            )}
                            control={control}
                            rules={{ required: false }}
                            name={typeName}
                        />
                        <div className="invalid-feedback">{errors[typeName]?.message}</div>
                    </div>
                    <div className="col-md-4 form-group">
                        <label className="control-label inputlabel">{t('paymentAmount')}{suffix}</label>
                        <input
                            name={amountName}
                            type="number"
                            step="0.01"
                            {...register(amountName)}
                            onBlur={onBlurAmount}
                            disabled={isFieldDisabled(amountName)}
                            className={`form-control ${errors[amountName] ? 'is-invalid' : ''}`}
                        />
                        <div className="invalid-feedback">{errors[amountName]?.message}</div>
                    </div>
                    <div className="col-md-6 form-group d-flex align-items-end">
                        <div className="form-check d-flex align-items-center gap-2 pb-1">
                            <input
                                id={`${preloadName}Check`}
                                name={preloadName}
                                type="checkbox"
                                {...register(preloadName, { required: false })}
                                onChange={onChangePreload}
                                disabled={isFieldDisabled(preloadName)}
                                className={`form-check-input ${errors[preloadName] ? 'is-invalid' : ''}`}
                            />
                            <label className="form-check-label inputlabel mb-0" htmlFor={`${preloadName}Check`}>
                                {t('PreloadTransaction')}
                            </label>
                            <div className="invalid-feedback">{errors[preloadName]?.message}</div>
                        </div>
                    </div>
                    <div className="col-md-6 form-group">
                        <label className="control-label inputlabel">{t('PreloadExpiration')}</label>
                        <input
                            name={expirationName}
                            type="number"
                            step="1"
                            {...register(expirationName)}
                            onBlur={onBlurExpiration}
                            disabled={isFieldDisabled(expirationName)}
                            className={`form-control ${errors[expirationName] ? 'is-invalid' : ''}`}
                        />
                        <div className="invalid-feedback">{errors[expirationName]?.message}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PaymentMethodCard;
